<script setup lang="ts">
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import { useLoader } from '~/composables/use-loader';
import { apiStartOnboarding } from '~/api/onboarding';
import { useAccountStore } from '~/store/account';
import { BusinessResponseDto } from '~/api/businesses';
import { computed } from '#imports';

const props = defineProps<{
  business: BusinessResponseDto;
  imageUrl: string | null;
  introductionPageName: string;
}>();

const emit = defineEmits<{
  'get-started': [];
}>();

const { refreshCurrentAccount } = useAccountStore();

const { t, tm, rt } = useI18n();

const paragraphs = computed<string[]>(() => {
  const descriptionKey = `${props.introductionPageName}.description`;

  const items = tm(descriptionKey);

  if (!Array.isArray(items)) {
    throw new TypeError('i18n "description" key must be an array');
  }

  return items.map((item) => rt(item));
});

const onGetStarted = async () => {
  if (props.business.onboardingStartedAt !== null) {
    emit('get-started');
    return;
  }
  await useLoader({
    async action() {
      await apiStartOnboarding();
      await refreshCurrentAccount();

      emit('get-started');
    },
  });
};
</script>

<template>
  <div class="md:relative md:h-screen">
    <img v-if="props.imageUrl" :src="props.imageUrl" class="w-full md:h-full" />

    <div
      class="md:absolute md:inset-0 md:flex md:items-center md:justify-center"
    >
      <div
        class="md:bg-surface md:rounded-2xl pb-10 px-10 md:px-14 md:max-h-[90%] md:max-w-[930px] md:overflow-y-auto"
      >
        <h1 class="pt-5 md:pt-12 font-bold text-xl md:text-5xl md:text-center">
          {{ t(`${props.introductionPageName}.header`) }}
        </h1>

        <div class="pt-4 md:pt-6 space-y-4">
          <p v-for="paragraph in paragraphs" :key="paragraph">
            {{ paragraph }}
          </p>
        </div>

        <div
          class="flex justify-center gap-3 md:gap-5 pt-10 flex-col md:flex-row-reverse"
        >
          <Btn
            :variant="BtnVariant.FILLED"
            :color="BtnColor.PRIMARY"
            :size="BtnSize.LG"
            @click="onGetStarted"
          >
            {{ t('get-started') }}
          </Btn>
          <Btn
            :variant="BtnVariant.OUTLINED"
            :color="BtnColor.PRIMARY"
            :size="BtnSize.LG"
            to="/"
          >
            {{ t('remind-me-later') }}
          </Btn>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en-US": {
    "remind-me-later": "Remind Me Later",
    "get-started": "Get Started",
    "key1": {
      "header": "Welcome to the ESJ Beauty Co-op Advertising Program",
      "description": [
        "We're thrilled to introduce you to an exceptional new opportunity: As a member of ESJ Beauty, you gain exclusive access to our innovative Co-op Social Media Advertising Program. As a valued member, you're invited to embark on a journey that will elevate your online presence and amplify your results like never before.",
        "You’ll experience the pinnacle of service with our white-glove approach to social media advertising. Get ready to stand out in the digital landscape as we craft and execute a tailored campaign based on your Ad preference and target demographics. Welcome to the future of social media advertising. Let’s elevate your practice together!"
      ]
    },
    "rxsight": {
      "header": "Welcome to the RxSight® Partner Marketing Program",
      "description": [
        "We're excited to help your practice stand out with a personalized, branded landing page!",
        "This page will seamlessly guide patients from our physician finder to your landing page, simplifying the process of booking consultations.",
        "On the next screen, you'll have the opportunity to upload variations of your logo to further personalize your page. Rest assured, the details you've already provided-like your booking link and practice information - will be incorporated for a cohesive and professional design.",
        "Let's get started on building your practice's customized patient experience!"
      ]
    },
    "lpg-endermologie": {
      "header": "Welcome to the LPG Endermologie Co-op Advertising Program",
      "description": [
        "We’re thrilled to invite you to a new era in beauty, wellness, health, and aesthetics. As a valued partner, you now have access to our exclusive Co-op Social Media Advertising Program — a powerful opportunity to grow your business, attract new clients, and strengthen your brand presence.",
        "With decades of proven innovation and trusted results, our solutions set the standard for non-invasive skin, body, and wellness treatments. Now, we’re extending that expertise to your marketing efforts.",
        "Our program offers personalized, white-glove support to help you create compelling, effective campaigns tailored to your brand. Every ad is designed to resonate with your audience, showcase your unique services, and drive real results — more bookings, more loyalty, and more revenue.",
        "Don’t just keep up with the market — lead it. Let’s grow your business and redefine success in beauty, wellness, and aesthetics — together."
      ]
    }
  },
  "fr-FR": {},
  "es-ES": {},
  "pt-PT": {},
  "de-DE": {}
}
</i18n>
